import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { QueryState } from "@/shared/ui/QueryState";
import { Card, Button } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { HelpCircle } from "lucide-react";
import { useState } from "react";
import { useAuditSummaryStatsQuery } from "../api/queries";
import { AuditSummaryStatsCards } from "../ui/AuditSummaryStatsCards";
import { AuditSummaryCharts } from "../ui/AuditSummaryCharts";
import { AuditSummaryFrequentlyFailed } from "@/modules/internal-audit/presentation/ui/AuditSummaryFrequentlyFailed";
import { AuditSummaryDepartments } from "../ui/AuditSummaryDepartments";
import { AuditSummaryPeriodComparison } from "../ui/AuditSummaryPeriodComparison";
import { AuditSummaryTrends } from "../ui/AuditSummaryTrends";
import { AuditSummaryFilter } from "../ui/AuditSummaryFilter";
import { AuditSummaryTour, useAuditSummaryTour } from "../tour/AuditSummaryTour";

export function AuditSummaryPage() {
  const nav = useNavigate();
  const canRead = useActionPermission(["internal_audit:summary:read"]);
  const tour = useAuditSummaryTour();

  const [filters, setFilters] = useState<{
    startDate?: string;
    endDate?: string;
    departmentId?: string;
  }>({});

  const { data: stats, isLoading, error } = useAuditSummaryStatsQuery(filters);

  if (!canRead) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ທ່ານບໍ່ມີສິດເຂົ້າເຖິງ</p>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <AuditSummaryTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="space-y-6">
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="font-bold text-2xl">ສະຫຼຸບຜົນການກວດສອບ</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="text-muted-foreground">
              ສະຫຼຸບຜົນການກວດສອບພາຍໃນ ແລະ ເບິ່ງແນວໂນ້ມ
            </p>
          </div>

          <AuditSummaryFilter
            filters={filters}
            onFiltersChange={setFilters}
          />

          <QueryState
            isLoading={isLoading}
            isError={!!error}
            error={error}
            isEmpty={!stats || stats.overall.totalAudits === 0}
            loadingFallback={
              <div className="flex min-h-[400px] items-center justify-center">
                <div className="text-center">
                  <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
                  <p className="mt-2 text-muted-foreground">ກຳລັງໂຫຼດ...</p>
                </div>
              </div>
            }
            errorFallback={() => (
              <Card className="p-12 text-center">
                <p className="text-muted-foreground text-lg">
                  ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຂໍ້ມູນ
                </p>
              </Card>
            )}
            emptyFallback={
              <Card className="p-12 text-center">
                <p className="text-muted-foreground text-lg">
                  ບໍ່ມີຂໍ້ມູນການກວດສອບໃນຊ່ວງເວລາທີ່ເລືອກ
                </p>
              </Card>
            }
          >
            {stats && (
              <>
                {/* Overall Statistics Cards */}
                <AuditSummaryStatsCards
                  stats={stats}
                  onViewUnresolvedIssues={() =>
                    nav({
                      to: "/app/internal-audit/issues",
                      search: {
                        offset: 0,
                        limit: 20,
                        sort: undefined,
                        filters: [
                          {
                            field: "status",
                            op: "in",
                            value: ["open", "in_progress"],
                          },
                        ],
                      },
                    })
                  }
                />

                {/* Pass/Fail Chart */}
                <AuditSummaryCharts stats={stats} />

                {/* Period Comparison */}
                <AuditSummaryPeriodComparison stats={stats} />

                {/* Trends */}
                <AuditSummaryTrends stats={stats} />

                {/* Frequently Failed Items */}
                <AuditSummaryFrequentlyFailed stats={stats} />

                {/* Departments with Problems */}
                <AuditSummaryDepartments
                  stats={stats}
                  onDepartmentClick={(departmentId) =>
                    nav({
                      to: "/app/internal-audit/issues",
                      search: {
                        offset: 0,
                        limit: 20,
                        sort: undefined,
                        filters: [
                          {
                            field: "departmentId",
                            op: "eq",
                            value: departmentId,
                          },
                        ],
                      },
                    })
                  }
                />
              </>
            )}
          </QueryState>
        </div>
      </Main>
    </>
  );
}

